/**
 * Prospection — the board.
 *
 * One row per prospect (a customer or a lead the team is working), read left
 * to right the way a rep asks the questions: how hot is it (the score ring),
 * who is it, where does it stand (stage), what did the phone give, what did
 * email give, what is planned next and is it late, who owns it, who touched
 * it last — and whether someone has it open right now, so two reps never
 * call the same prospect at the same time.
 *
 * On top, the figures for me and for the team; on the left, the filters that
 * turn the board into a call list ("mine, due today", "unassigned leads",
 * "dormant customers nobody called").
 *
 * A plain ItemView like the worklist: the unit is a prospect row rendered
 * from one JSON page, and every change on a prospect comes back as that
 * prospect's row, patched in place — no table framework, no refetch.
 */
define('prospectionBoardView',[
  'require',
  'module',
  'app',
  'marionette',
  'underscore',
  'jquery',
  'settings',
  'entities/prospection'
], function (require, Module, App, Marionette, _, $, Settings) {
  'use strict';

  var FILTER_STORAGE_KEY = 'prospection.boardFilters';
  var PAGE_SIZE = 50;
  // The kanban shows every card at once (grouped by stage), within reason.
  var KANBAN_PAGE = 200;
  var SCORE_FULL = 100;
  var UNASSIGNED = '__none__';

  var SCOPES = [
    { key: 'all', label: _.i18n('prospection.scope.all', null, 'All') },
    { key: 'mine', label: _.i18n('prospection.scope.mine', null, 'Mine') },
    { key: 'unassigned', label: _.i18n('prospection.scope.unassigned', null, 'Unassigned') }
  ];

  var STAGES = [
    { key: 'NEW', label: _.i18n('prospection.stage.NEW', null, 'New'), tone: 'new' },
    { key: 'CONTACTED', label: _.i18n('prospection.stage.CONTACTED', null, 'Contacted'), tone: 'contacted' },
    { key: 'INTERESTED', label: _.i18n('prospection.stage.INTERESTED', null, 'Interested'), tone: 'interested' },
    { key: 'QUOTE_SENT', label: _.i18n('prospection.stage.QUOTE_SENT', null, 'Quote sent'), tone: 'quote' },
    { key: 'ON_HOLD', label: _.i18n('prospection.stage.ON_HOLD', null, 'On hold'), tone: 'hold' },
    { key: 'WON', label: _.i18n('prospection.stage.WON', null, 'Won'), tone: 'won' },
    { key: 'LOST', label: _.i18n('prospection.stage.LOST', null, 'Lost'), tone: 'lost' }
  ];

  var PHONE = [
    { key: 'NOT_CALLED', label: _.i18n('prospection.phone.NOT_CALLED', null, 'Not called'), tone: 'none' },
    { key: 'NO_ANSWER', label: _.i18n('prospection.phone.NO_ANSWER', null, 'No answer'), tone: 'try' },
    { key: 'VOICEMAIL', label: _.i18n('prospection.phone.VOICEMAIL', null, 'Voicemail'), tone: 'try' },
    { key: 'CALLBACK', label: _.i18n('prospection.phone.CALLBACK', null, 'Call back'), tone: 'warm' },
    { key: 'REACHED', label: _.i18n('prospection.phone.REACHED', null, 'Reached'), tone: 'ok' },
    { key: 'REFUSED', label: _.i18n('prospection.phone.REFUSED', null, 'Refused'), tone: 'bad' },
    { key: 'WRONG_NUMBER', label: _.i18n('prospection.phone.WRONG_NUMBER', null, 'Wrong number'), tone: 'bad' }
  ];

  var EMAIL = [
    { key: 'NOT_SENT', label: _.i18n('prospection.email.NOT_SENT', null, 'Not sent'), tone: 'none' },
    { key: 'SENT', label: _.i18n('prospection.email.SENT', null, 'Sent'), tone: 'try' },
    { key: 'FOLLOW_UP', label: _.i18n('prospection.email.FOLLOW_UP', null, 'Follow-up sent'), tone: 'try' },
    { key: 'REPLIED', label: _.i18n('prospection.email.REPLIED', null, 'Replied'), tone: 'ok' },
    { key: 'BOUNCED', label: _.i18n('prospection.email.BOUNCED', null, 'Bounced'), tone: 'bad' },
    { key: 'REFUSED', label: _.i18n('prospection.email.REFUSED', null, 'Refused'), tone: 'bad' }
  ];

  var DUE = [
    { key: '', label: _.i18n('prospection.due.any', null, 'Any next action') },
    { key: 'OVERDUE', label: _.i18n('prospection.due.overdue', null, 'Overdue') },
    { key: 'TODAY', label: _.i18n('prospection.due.today', null, 'Due today') },
    { key: 'WEEK', label: _.i18n('prospection.due.week', null, 'Due this week') },
    { key: 'NONE', label: _.i18n('prospection.due.none', null, 'Nothing planned') }
  ];

  var SEGMENTS = [
    { key: '', label: _.i18n('prospection.segment.any', null, 'Leads and customers') },
    { key: 'LEAD', label: _.i18n('prospection.segment.lead', null, 'Leads only') },
    { key: 'NEVER_ORDERED', label: _.i18n('prospection.segment.neverOrdered', null, 'Never ordered') },
    { key: 'CUSTOMER', label: _.i18n('prospection.segment.customer', null, 'Existing customers') },
    { key: 'DORMANT', label: _.i18n('prospection.segment.dormant', null, 'Dormant customers (no order for a year)') }
  ];

  var SORTS = [
    { key: 'score', label: _.i18n('prospection.sort.score', null, 'Priority') },
    { key: 'nextAction', label: _.i18n('prospection.sort.nextAction', null, 'Next action') },
    { key: 'lastActivity', label: _.i18n('prospection.sort.lastActivity', null, 'Last activity') },
    { key: 'lastOrder', label: _.i18n('prospection.sort.lastOrder', null, 'Last order') },
    { key: 'ordersTotal', label: _.i18n('prospection.sort.ordersTotal', null, 'Turnover') },
    { key: 'name', label: _.i18n('prospection.sort.name', null, 'Name') },
    { key: 'created', label: _.i18n('prospection.sort.created', null, 'Enrolled') }
  ];

  var VIEWS = [
    { key: 'list', label: _.i18n('prospection.view.list', null, 'List'), icon: 'fa-list' },
    { key: 'kanban', label: _.i18n('prospection.view.kanban', null, 'Kanban'), icon: 'fa-columns' }
  ];

  var NEXT_ICON = { CALL: 'fa-phone', EMAIL: 'fa-envelope-o', MEETING: 'fa-calendar', OTHER: 'fa-flag-o' };

  function byKey(list, key) {
    return _.findWhere(list, { key: key || '' }) || null;
  }

  function loadFilters() {
    try {
      var raw = window.localStorage ? window.localStorage.getItem(FILTER_STORAGE_KEY) : null;
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }

  function saveFilters(filters) {
    try {
      if (window.localStorage) {
        window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters || {}));
      }
    } catch (e) { /* ignore quota / privacy-mode errors */ }
  }

  function currentUserSecId() {
    var user = Settings.get('currentUser');
    return user ? (user.secId || null) : null;
  }

  function userDisplayName(u) {
    if (!u) { return ''; }
    var full = $.trim(((u.firstName || '') + ' ' + (u.lastName || '')).replace(/\s+/g, ' '));
    return u.fullName || full || u.email || u.logonId || u.secId || '';
  }

  function initials(name) {
    if (!name) { return '?'; }
    var parts = $.trim(name).split(/\s+/);
    if (parts.length === 1) { return parts[0].slice(0, 2).toUpperCase(); }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  function avatarColor(name) {
    var str = String(name || '');
    var hash = 0;
    for (var i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) % 997;
    }
    return 'c' + (hash % 10);
  }

  function formatDate(value) {
    if (!value) { return ''; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    return d.toLocaleString('fr-BE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  }

  function formatDay(value) {
    if (!value) { return ''; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    return d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  function ageLabel(value) {
    if (!value) { return ''; }
    var t = new Date(value).getTime();
    if (isNaN(t)) { return ''; }
    var h = (Date.now() - t) / 36e5;
    if (h < 1) { return Math.max(1, Math.round(h * 60)) + 'm'; }
    if (h < 24) { return Math.round(h) + 'h'; }
    return Math.floor(h / 24) + 'd';
  }

  // "in 3d", "today", "2d late": the next action told relative to now.
  function dueLabel(value) {
    if (!value) { return ''; }
    var t = new Date(value).getTime();
    if (isNaN(t)) { return ''; }
    var days = Math.floor((t - Date.now()) / 864e5);
    var todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    if (t < todayStart.getTime()) {
      var late = Math.max(1, Math.ceil((todayStart.getTime() - t) / 864e5));
      return late + 'd ' + _.i18n('prospection.due.late', null, 'late');
    }
    if (t < todayStart.getTime() + 864e5) { return _.i18n('prospection.due.todayShort', null, 'today'); }
    if (days <= 1) { return _.i18n('prospection.due.tomorrow', null, 'tomorrow'); }
    return _.i18n('prospection.due.in', null, 'in') + ' ' + days + 'd';
  }

  function formatAmount(value) {
    if (value === null || value === undefined || value === '') { return ''; }
    var v = Math.round(Number(value) || 0);
    return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' €';
  }

  function gaugeFill(score) {
    return Math.max(4, Math.min(100, Math.round((score || 0) / SCORE_FULL * 100)));
  }

  function gaugeHeat(score) {
    if (score >= 60) { return 'hot'; }
    if (score >= 35) { return 'warm'; }
    return 'cool';
  }

  // The second line under the name: what the prospect is, in a few words.
  function subLine(p) {
    var bits = [];
    if (p.companyName && p.companyName !== p.customerName) { bits.push(p.companyName); }
    var person = $.trim((p.customerFirstName || '') + ' ' + (p.customerLastName || ''));
    if (person) { bits.push(person); }
    if (p.customerEmail) { bits.push(p.customerEmail); }
    if (p.customerTel) { bits.push(p.customerTel); }
    if (p.customerWebShopLocale) {
      bits.push(p.customerWebShopLocale);
    } else if (p.customerCountryCode) {
      bits.push(String(p.customerCountryCode).toUpperCase());
    }
    return bits.join(' · ');
  }

  // Why it matters: size and money in one short line for the potential column.
  function potentialLine(p) {
    var bits = [];
    var employees = p.companyEmployees || p.customerNbrEmployees;
    if (employees) { bits.push(employees + ' ' + _.i18n('prospection.employees', null, 'empl.')); }
    if (p.companyIndustry) { bits.push(p.companyIndustry); }
    if (p.ordersCount) {
      bits.push(p.ordersCount + ' ' + _.i18n('prospection.orders', null, 'orders') +
        (p.ordersTotal ? ' · ' + formatAmount(p.ordersTotal) : ''));
    } else if (p.offersCount) {
      bits.push(p.offersCount + ' ' + _.i18n('prospection.offers', null, 'offers'));
    }
    return bits.join(' · ');
  }

  function potentialTitle(p) {
    var lines = [];
    if (p.companyEmployees) { lines.push('Company: ' + p.companyEmployees + ' employees'); }
    if (p.companyAnnualRevenue) { lines.push('Annual revenue: ' + formatAmount(p.companyAnnualRevenue)); }
    if (p.ordersCount) {
      lines.push('Orders: ' + p.ordersCount + ' (' + formatAmount(p.ordersTotal || 0) + ')' +
        (p.lastOrderAt ? ', last ' + formatDay(p.lastOrderAt) : ''));
    }
    if (p.offersCount) {
      lines.push('Offers: ' + p.offersCount + (p.openOffersCount ? ', ' + p.openOffersCount + ' in the last 90 days' : ''));
    }
    if (p.lastEmailInAt) { lines.push('Last email from them: ' + formatDay(p.lastEmailInAt)); }
    if (p.lastEmailOutAt) { lines.push('Last email to them: ' + formatDay(p.lastEmailOutAt)); }
    return lines.join('\n') || 'No history yet';
  }

  var ROW_TPL = _.template(
    '<li class="pb-row" tabindex="0" role="button" data-secid="<%= secId %>">' +
    '  <span class="pb-gauge <%= heat %>" style="--p:<%= fill %>" title="<%= scoreReason %>"><i><%= score %></i></span>' +
    '  <span class="pb-id"><span class="pb-av <%= avatarColor %>"><%= avatar %></span>' +
    '  <span class="pb-who">' +
    '    <span class="pb-nm"><span class="pb-nm-t"><%= name %></span>' +
    '      <% if (isLead) { %><span class="pb-tag lead"><%= leadLabel %></span><% } %>' +
    '      <% if (vip) { %><span class="pb-tag vip">VIP</span><% } %>' +
    '      <% if (viewer) { %><span class="pb-eye" title="<%= viewerTitle %>"><i class="fa fa-eye"></i><%= viewer %></span><% } %>' +
    '    </span>' +
    '    <span class="pb-sb" title="<%= sub %>"><%= sub %></span>' +
    '  </span></span>' +
    '  <span class="pb-pot" title="<%= potentialTitle %>"><%= potential %></span>' +
    '  <span class="pb-stage st-<%= stageTone %>"><%= stage %></span>' +
    '  <span class="pb-chan phone <%= phoneTone %>" title="<%= phoneTitle %>">' +
    '    <i class="fa fa-phone"></i><b><%= phone %></b><% if (phoneAttempts) { %><em>×<%= phoneAttempts %></em><% } %>' +
    '    <% if (phoneAge) { %><small><%= phoneAge %></small><% } %></span>' +
    '  <span class="pb-chan mail <%= emailTone %>" title="<%= emailTitle %>">' +
    '    <i class="fa fa-envelope-o"></i><b><%= email %></b><% if (emailAttempts) { %><em>×<%= emailAttempts %></em><% } %>' +
    '    <% if (emailAge) { %><small><%= emailAge %></small><% } %></span>' +
    '  <span class="pb-next <%= nextTone %>" title="<%= nextTitle %>">' +
    '    <% if (next) { %><i class="fa <%= nextIcon %>"></i><b><%= next %></b><% } else { %><span class="pb-next-none">—</span><% } %></span>' +
    '  <span class="pb-owner">' +
    '    <% if (owner) { %><span class="pb-uav <%= ownerColor %>" title="<%= ownerTitle %>"><%= owner %></span>' +
    '    <% } else { %><button type="button" class="pb-claim js-claim" title="<%= claimTitle %>"><%= claimLabel %></button><% } %>' +
    '  </span>' +
    '  <span class="pb-last" title="<%= lastTitle %>"><%= last %></span>' +
    '</li>'
  );

  // A kanban card: the row folded into a small block. Same facts, less room.
  var CARD_TPL = _.template(
    '<li class="pb-card" tabindex="0" role="button" draggable="true" data-secid="<%= secId %>">' +
    '  <div class="pb-card-top">' +
    '    <span class="pb-gauge sm <%= heat %>" style="--p:<%= fill %>" title="<%= scoreReason %>"><i><%= score %></i></span>' +
    '    <span class="pb-card-who">' +
    '      <span class="pb-nm"><span class="pb-nm-t"><%= name %></span>' +
    '        <% if (isLead) { %><span class="pb-tag lead"><%= leadLabel %></span><% } %>' +
    '        <% if (vip) { %><span class="pb-tag vip">VIP</span><% } %></span>' +
    '      <span class="pb-sb" title="<%= sub %>"><%= sub %></span>' +
    '    </span>' +
    '    <% if (owner) { %><span class="pb-uav sm <%= ownerColor %>" title="<%= ownerTitle %>"><%= owner %></span><% } %>' +
    '  </div>' +
    '  <div class="pb-card-chans">' +
    '    <span class="pb-chan phone <%= phoneTone %>" title="<%= phoneTitle %>"><i class="fa fa-phone"></i><b><%= phone %></b><% if (phoneAttempts) { %><em>×<%= phoneAttempts %></em><% } %></span>' +
    '    <span class="pb-chan mail <%= emailTone %>" title="<%= emailTitle %>"><i class="fa fa-envelope-o"></i><b><%= email %></b><% if (emailAttempts) { %><em>×<%= emailAttempts %></em><% } %></span>' +
    '  </div>' +
    '  <div class="pb-card-foot">' +
    '    <span class="pb-next <%= nextTone %>" title="<%= nextTitle %>"><% if (next) { %><i class="fa <%= nextIcon %>"></i><b><%= next %></b><% } %></span>' +
    '    <% if (viewer) { %><span class="pb-eye" title="<%= viewerTitle %>"><i class="fa fa-eye"></i><%= viewer %></span><% } %>' +
    '    <span class="pb-grow"></span>' +
    '    <span class="pb-last" title="<%= lastTitle %>"><%= last %></span>' +
    '  </div>' +
    '</li>'
  );

  var COLUMN_TPL = _.template(
    '<section class="pb-col st-<%= tone %>" data-stage="<%= stage %>">' +
    '  <header class="pb-col-head"><span class="pb-col-dot"></span><b><%= label %></b><span class="pb-col-n"><%= count %></span></header>' +
    '  <ul class="pb-col-cards"><%= cards %></ul>' +
    '</section>'
  );

  Module.exports = Marionette.ItemView.extend({
    className: 'prospection-board-view',

    template: _.template(
      '<div class="pb-top">' +
      '  <div class="pb-kpis js-kpis"></div>' +
      '</div>' +
      '<div class="pb-bar">' +
      '  <% _.each(scopes, function (s) { %>' +
      '  <button type="button" class="pb-tab js-scope <%= s.key === scope ? "on" : "" %>" data-scope="<%= s.key %>"><%= s.label %><span class="n"></span></button>' +
      '  <% }); %>' +
      '  <span class="pb-sep"></span>' +
      '  <span class="pb-views">' +
      '  <% _.each(views, function (v) { %>' +
      '  <button type="button" class="pb-tab quiet js-view <%= v.key === view ? "on" : "" %>" data-view="<%= v.key %>" title="<%= v.label %>"><i class="fa <%= v.icon %>"></i></button>' +
      '  <% }); %>' +
      '  </span>' +
      '  <span class="pb-sep"></span>' +
      '  <span class="pb-select"><select class="js-due">' +
      '    <% _.each(dues, function (d) { %><option value="<%= d.key %>" <%= d.key === due ? "selected" : "" %>><%= d.label %></option><% }); %>' +
      '  </select></span>' +
      '  <span class="pb-select"><select class="js-segment">' +
      '    <% _.each(segments, function (d) { %><option value="<%= d.key %>" <%= d.key === segment ? "selected" : "" %>><%= d.label %></option><% }); %>' +
      '  </select></span>' +
      '  <span class="pb-stages js-stages"></span>' +
      '  <span class="pb-select"><select class="js-phone">' +
      '    <option value=""><%= anyPhone %></option>' +
      '    <% _.each(phones, function (d) { %><option value="<%= d.key %>" <%= d.key === phone ? "selected" : "" %>><%= d.label %></option><% }); %>' +
      '  </select></span>' +
      '  <span class="pb-select"><select class="js-email">' +
      '    <option value=""><%= anyEmail %></option>' +
      '    <% _.each(emails, function (d) { %><option value="<%= d.key %>" <%= d.key === email ? "selected" : "" %>><%= d.label %></option><% }); %>' +
      '  </select></span>' +
      '  <span class="pb-who-filter js-webshops"></span>' +
      '  <span class="pb-who-filter js-assignees"></span>' +
      '  <label class="pb-toggle"><input type="checkbox" class="js-closed" <%= closed ? "checked" : "" %>/><%= closedLabel %></label>' +
      '  <span class="pb-grow"></span>' +
      '  <span class="pb-select sort"><i class="fa fa-sort-amount-desc"></i><select class="js-sort">' +
      '    <% _.each(sorts, function (d) { %><option value="<%= d.key %>" <%= d.key === sort ? "selected" : "" %>><%= d.label %></option><% }); %>' +
      '  </select></span>' +
      '  <span class="pb-find"><i class="fa fa-search"></i><input type="text" class="js-search" placeholder="<%= searchPlaceholder %>" value="<%= search %>"/></span>' +
      '  <button type="button" class="pb-btn quiet js-activity" style="display:none" title="<%= activityTitle %>"><i class="fa fa-bar-chart"></i> <%= activityLabel %></button>' +
      '  <button type="button" class="pb-btn js-add"><i class="fa fa-plus"></i> <%= addLabel %></button>' +
      '  <button type="button" class="pb-reload js-refresh" title="<%= refreshLabel %>">⟲</button>' +
      '</div>' +
      '<div class="pb-add js-add-panel" style="display:none">' +
      '  <div class="pb-add-row">' +
      '    <input type="text" class="js-add-search" placeholder="<%= addSearchPlaceholder %>"/>' +
      '    <button type="button" class="pb-btn quiet js-enroll-leads"><i class="fa fa-user-plus"></i> <%= enrollLeadsLabel %> <span class="js-leads-n"></span></button>' +
      '    <button type="button" class="pb-btn quiet js-sync" title="<%= syncTitle %>"><i class="fa fa-refresh"></i> <%= syncLabel %></button>' +
      '    <label class="pb-btn quiet pb-import" title="<%= importTitle %>"><i class="fa fa-upload"></i> <%= importLabel %>' +
      '      <input type="file" class="js-import" accept=".xlsx,.xls,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"/></label>' +
      '    <a class="pb-link js-import-template" href="#"><%= importTemplateLabel %></a>' +
      '    <button type="button" class="pb-x js-add-close">×</button>' +
      '  </div>' +
      '  <div class="pb-import-result js-import-result" style="display:none"></div>' +
      '  <ul class="pb-cands js-cands"></ul>' +
      '</div>' +
      '<div class="pb-head" <%= view === "kanban" ? "style=display:none" : "" %>>' +
      '  <span class="h-score"><%= hScore %></span><span class="h-who"><%= hWho %></span><span class="h-pot"><%= hPot %></span>' +
      '  <span class="h-stage"><%= hStage %></span><span class="h-phone"><%= hPhone %></span><span class="h-mail"><%= hMail %></span>' +
      '  <span class="h-next"><%= hNext %></span><span class="h-owner"><%= hOwner %></span><span class="h-last"><%= hLast %></span>' +
      '</div>' +
      '<ul class="pb-body js-body <%= view === "kanban" ? "is-kanban" : "" %>"></ul>' +
      '<div class="pb-footer js-footer"></div>'
    ),

    ui: {
      body: '.js-body',
      footer: '.js-footer',
      kpis: '.js-kpis',
      search: '.js-search',
      addPanel: '.js-add-panel',
      addSearch: '.js-add-search',
      cands: '.js-cands',
      importResult: '.js-import-result',
      leadsN: '.js-leads-n'
    },

    events: {
      'click .js-scope': 'onScope',
      'click .js-view': 'onView',
      'change .js-due': 'onDue',
      'change .js-segment': 'onSegment',
      'change .js-phone': 'onPhone',
      'change .js-email': 'onEmail',
      'change .js-closed': 'onClosed',
      'change .js-sort': 'onSortChange',
      'click .js-refresh': 'onRefresh',
      'keyup .js-search': 'onSearchKey',
      'click .js-activity': 'onActivity',
      'click .js-add': 'onToggleAdd',
      'click .js-add-close': 'onToggleAdd',
      'keyup .js-add-search': 'onAddSearchKey',
      'click .js-cand': 'onCandidateClick',
      'click .js-enroll-leads': 'onEnrollLeads',
      'click .js-sync': 'onSync',
      'change .js-import': 'onImport',
      'click .js-import-template': 'onImportTemplate',
      'click .js-kpi': 'onKpiClick',
      'click .pb-row': 'onRowClick',
      'keydown .pb-row': 'onRowKey',
      'click .js-claim': 'onClaim',
      'click .js-more': 'onMore',
      'click .pb-card': 'onRowClick',
      'keydown .pb-card': 'onRowKey',
      'dragstart .pb-card': 'onDragStart',
      'dragend .pb-card': 'onDragEnd',
      'dragover .pb-col': 'onDragOver',
      'dragleave .pb-col': 'onDragLeave',
      'drop .pb-col': 'onDrop'
    },

    initialize: function () {
      this.filters = loadFilters();
      this.page = 1;
      this.items = [];
      this.total = 0;
      this.kpiFilters = {};
      this.listenTo(App, 'prospection:updated', this.onProspectUpdated);
      this.listenTo(App, 'prospection:removed', this.onProspectRemoved);
      this.listenTo(App, 'prospection:enrolled', this.onProspectEnrolled);
    },

    serializeData: function () {
      var f = this.filters;
      return {
        scopes: SCOPES,
        scope: f.scope || 'all',
        views: VIEWS,
        view: f.view || 'list',
        dues: DUE,
        due: f.due || '',
        segments: SEGMENTS,
        segment: f.segment || '',
        phones: PHONE,
        phone: f.phone || '',
        emails: EMAIL,
        email: f.email || '',
        sorts: SORTS,
        sort: f.sort || 'score',
        closed: !!f.closed,
        search: f.search || '',
        anyPhone: _.i18n('prospection.filter.anyPhone', null, 'Any phone status'),
        anyEmail: _.i18n('prospection.filter.anyEmail', null, 'Any email status'),
        closedLabel: _.i18n('prospection.filter.closed', null, 'Won / lost'),
        searchPlaceholder: _.i18n('prospection.filter.search', null, 'Name, company, email, phone'),
        addLabel: _.i18n('prospection.add', null, 'Add'),
        activityLabel: _.i18n('prospection.activity.short', null, 'Activity'),
        activityTitle: _.i18n('prospection.activity.title', null, 'Prospecting activity') + ' — ' + _.i18n('prospection.activity.perUser', null, 'per user'),
        refreshLabel: _.i18n('common.refresh', null, 'Refresh'),
        addSearchPlaceholder: _.i18n('prospection.add.search', null, 'Find a customer or a lead to add…'),
        enrollLeadsLabel: _.i18n('prospection.add.allLeads', null, 'Add every lead'),
        syncLabel: _.i18n('prospection.sync', null, 'Recompute scores'),
        syncTitle: _.i18n('prospection.sync.title', null, 'Enrol the new leads and refresh orders, offers and emails of every prospect'),
        importLabel: _.i18n('prospection.import', null, 'Import a file'),
        importTitle: _.i18n('prospection.import.title', null, 'Excel or CSV: each line is matched to a customer (code, then email) or created as a lead, then added to the board'),
        importTemplateLabel: _.i18n('prospection.import.template', null, 'Template'),
        hScore: _.i18n('prospection.col.score', null, 'Score'),
        hWho: _.i18n('prospection.col.prospect', null, 'Prospect'),
        hPot: _.i18n('prospection.col.potential', null, 'Potential / history'),
        hStage: _.i18n('prospection.col.stage', null, 'Stage'),
        hPhone: _.i18n('prospection.col.phone', null, 'Phone'),
        hMail: _.i18n('prospection.col.email', null, 'Email'),
        hNext: _.i18n('prospection.col.next', null, 'Next action'),
        hOwner: _.i18n('prospection.col.owner', null, 'Owner'),
        hLast: _.i18n('prospection.col.last', null, 'Last activity')
      };
    },

    onShow: function () {
      this.renderStageFilter();
      this.renderWebshopFilter();
      this.renderAssigneeFilter();
      this.fetch();
      this.fetchSummary();
    },

    // ---- filters ----------------------------------------------------------
    renderStageFilter: function () {
      var self = this;
      var $host = this.$('.js-stages');
      require(['multiSelectDropdownView'], function (MultiSelectDropdown) {
        if (self.isDestroyed) { return; }
        self.stageDropdown = new MultiSelectDropdown({
          items: _.map(STAGES, function (s) { return { value: s.key, label: s.label }; }),
          selection: self.filters.stages || [],
          placeholder: _.i18n('prospection.filter.anyStage', null, 'Any stage'),
          countLabel: _.i18n('prospection.filter.stages', null, 'stages'),
          searchPlaceholder: _.i18n('prospection.filter.findStage', null, 'Find a stage'),
          onApply: function (picked) {
            self.filters.stages = (picked || []).slice();
            saveFilters(self.filters);
            self.fetch();
          }
        }).render();
        $host.append(self.stageDropdown.el);
      });
    },

    // "Webshop": the shop the customer belongs to (EndUser.webShopLocale), any
    // number at once — the list the customer form offers (Settings.getWebshopLocales).
    // Hidden on an account without webshops.
    renderWebshopFilter: function () {
      var self = this;
      var $host = this.$('.js-webshops');
      var shops = Settings.getWebshopLocales ? Settings.getWebshopLocales() : [];
      if (!shops.length) { $host.hide(); return; }
      require(['multiSelectDropdownView'], function (MultiSelectDropdown) {
        if (self.isDestroyed) { return; }
        self.webshopDropdown = new MultiSelectDropdown({
          items: _.map(_.sortBy(shops, 'code'), function (w) {
            return { value: w.code, label: w.code + ' — ' + String(w.value || '').replace(/^https?:\/\/(www\.)?/i, '') };
          }),
          selection: self.filters.webshops || [],
          placeholder: _.i18n('prospection.filter.anyWebshop', null, 'Any webshop'),
          countLabel: _.i18n('prospection.filter.webshops', null, 'webshops'),
          searchPlaceholder: _.i18n('prospection.filter.findWebshop', null, 'Find a webshop'),
          onApply: function (picked) {
            self.filters.webshops = (picked || []).slice();
            saveFilters(self.filters);
            self.fetch();
          }
        }).render();
        $host.append(self.webshopDropdown.el);
      });
    },

    renderAssigneeFilter: function () {
      var self = this;
      var $host = this.$('.js-assignees');
      require(['multiSelectDropdownView', 'entities/kanban_tickets'], function (MultiSelectDropdown) {
        if (self.isDestroyed) { return; }
        self.assigneeDropdown = new MultiSelectDropdown({
          items: [{ value: UNASSIGNED, label: _.i18n('prospection.scope.unassigned', null, 'Unassigned') }],
          selection: self.filters.assignees || [],
          placeholder: _.i18n('prospection.filter.anyone', null, 'Any owner'),
          countLabel: _.i18n('prospection.filter.owners', null, 'owners'),
          searchPlaceholder: _.i18n('prospection.filter.findUser', null, 'Find a user'),
          onApply: function (picked) {
            self.filters.assignees = (picked || []).slice();
            saveFilters(self.filters);
            self.fetch();
          }
        }).render();
        $host.append(self.assigneeDropdown.el);

        $.when(App.request('kanban-tickets:get-users')).done(function (users) {
          if (self.isDestroyed || !self.assigneeDropdown) { return; }
          self.users = users || [];
          var me = currentUserSecId();
          var items = [{ value: UNASSIGNED, label: _.i18n('prospection.scope.unassigned', null, 'Unassigned') }];
          var others = [];
          _.each(users || [], function (u) {
            if (!u || !u.secId) { return; }
            if (u.secId === me) {
              items.push({ value: u.secId, label: userDisplayName(u) + ' (' + _.i18n('common.me', null, 'me') + ')' });
            } else {
              others.push({ value: u.secId, label: userDisplayName(u) });
            }
          });
          self.assigneeDropdown.setItems(items.concat(_.sortBy(others, function (o) {
            return o.label.toLowerCase();
          })));
        });
      });
    },

    params: function () {
      var f = this.filters;
      var params = this.isKanban() ?
        { first: 0, pageSize: KANBAN_PAGE } :
        { first: (this.page - 1) * PAGE_SIZE, pageSize: PAGE_SIZE };
      if (f.scope && f.scope !== 'all') { params.scope = f.scope; }
      if (f.due) { params.due = f.due; }
      if (f.segment) { params.segment = f.segment; }
      if (f.phone) { params.phoneStatus = f.phone; }
      if (f.email) { params.emailStatus = f.email; }
      if (f.stages && f.stages.length) { params.stages = f.stages; }
      if (f.closed) { params.includeClosed = true; }
      if (f.webshops && f.webshops.length) { params.webShopLocales = f.webshops; }
      var picked = f.assignees || [];
      var users = _.without(picked, UNASSIGNED);
      if (users.length) { params.userSecIds = users; }
      if (_.contains(picked, UNASSIGNED)) { params.unassigned = true; }
      if (f.sort) { params.sort = f.sort; }
      if (f.search) { params.search = f.search; }
      return params;
    },

    // ---- data -------------------------------------------------------------
    fetch: function () {
      var self = this;
      this.page = 1;
      this.items = [];
      this.ui.body.html('<li class="pb-state">' + _.i18n('common.loading', null, 'Loading…') + '</li>');
      App.request('prospection:list', this.params()).done(function (page) {
        if (self.isDestroyed) { return; }
        self.items = page.items;
        self.total = page.total;
        self.renderBody();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        self.ui.body.html('<li class="pb-state pb-state-bad">' +
          _.i18n('prospection.loadError', null, 'Could not load the board. Refresh to try again.') + '</li>');
        self.ui.footer.empty();
      });
    },

    fetchMore: function () {
      var self = this;
      App.request('prospection:list', this.params()).done(function (page) {
        if (self.isDestroyed) { return; }
        self.items = self.items.concat(page.items);
        self.total = page.total;
        self.ui.body.append(_.map(page.items, self.rowHtml, self).join(''));
        self.renderFooter();
      });
    },

    fetchSummary: function () {
      var self = this;
      App.request('prospection:summary').done(function (summary) {
        if (self.isDestroyed) { return; }
        self.summary = summary || {};
        // Managers only: the backend refuses the screen to anyone else anyway.
        self.$('.js-activity').toggle(!!self.summary.manager);
        self.renderKpis();
        self.ui.leadsN.text(self.summary.unenrolledLeads ? '(' + self.summary.unenrolledLeads + ')' : '');
      });
    },

    // ---- rendering --------------------------------------------------------
    renderKpis: function () {
      var s = this.summary || {};
      var tiles = [
        { key: 'mineDue', n: s.mineDueToday || 0, label: _.i18n('prospection.kpi.mineDue', null, 'To do today'),
          sub: _.i18n('prospection.kpi.mine', null, 'mine'), tone: s.mineOverdue ? 'warn' : 'ok', filter: { scope: 'mine', due: 'TODAY' } },
        { key: 'mineOverdue', n: s.mineOverdue || 0, label: _.i18n('prospection.kpi.overdue', null, 'Late'),
          sub: _.i18n('prospection.kpi.mine', null, 'mine'), tone: s.mineOverdue ? 'bad' : 'ok', filter: { scope: 'mine', due: 'OVERDUE' } },
        { key: 'mineOpen', n: s.mineOpen || 0, label: _.i18n('prospection.kpi.mineOpen', null, 'My prospects'),
          sub: _.i18n('prospection.kpi.open', null, 'open'), tone: 'plain', filter: { scope: 'mine', due: '' } },
        { key: 'myContacts', n: s.myContactsThisWeek || 0, label: _.i18n('prospection.kpi.contacts', null, 'Contacts this week'),
          sub: (s.teamContactsThisWeek || 0) + ' ' + _.i18n('prospection.kpi.team', null, 'team'), tone: 'plain', filter: null },
        { key: 'unassigned', n: s.unassignedOpen || 0, label: _.i18n('prospection.kpi.unassigned', null, 'Unassigned'),
          sub: _.i18n('prospection.kpi.upForGrabs', null, 'up for grabs'), tone: s.unassignedOpen ? 'info' : 'plain', filter: { scope: 'unassigned', due: '' } },
        { key: 'teamOverdue', n: s.teamOverdue || 0, label: _.i18n('prospection.kpi.teamOverdue', null, 'Late'),
          sub: _.i18n('prospection.kpi.team', null, 'team'), tone: s.teamOverdue ? 'warn' : 'plain', filter: { scope: 'all', due: 'OVERDUE' } },
        { key: 'won', n: s.wonThisMonth || 0, label: _.i18n('prospection.kpi.won', null, 'Won this month'),
          sub: (s.teamOpen || 0) + ' ' + _.i18n('prospection.kpi.open', null, 'open'), tone: s.wonThisMonth ? 'good' : 'plain', filter: null }
      ];
      this.ui.kpis.html(_.map(tiles, function (t) {
        return '<button type="button" class="pb-kpi ' + t.tone + (t.filter ? ' js-kpi' : ' static') + '" data-key="' + t.key + '">' +
          '<b>' + t.n + '</b><span>' + _.escape(t.label) + '</span><small>' + _.escape(t.sub) + '</small></button>';
      }).join(''));
      this.kpiFilters = {};
      _.each(tiles, function (t) { if (t.filter) { this.kpiFilters[t.key] = t.filter; } }, this);
    },

    isKanban: function () {
      return this.filters.view === 'kanban';
    },

    renderBody: function () {
      this.ui.body.toggleClass('is-kanban', this.isKanban());
      this.$('.pb-head').toggle(!this.isKanban());
      if (this.isKanban()) {
        this.renderKanban();
        this.updateTabCount();
        return;
      }
      if (!this.items.length) {
        this.ui.body.html('<li class="pb-state"><span class="pb-state-icon">🎯</span>' +
          '<span class="pb-state-t">' + _.i18n('prospection.empty.title', null, 'Nothing here') + '</span>' +
          '<span class="pb-state-s">' + _.i18n('prospection.empty.text', null, 'No prospect matches these filters. Add customers or leads with the + button.') + '</span></li>');
        this.ui.footer.empty();
        this.updateTabCount();
        return;
      }
      this.ui.body.html(_.map(this.items, this.rowHtml, this).join(''));
      this.renderFooter();
      this.updateTabCount();
    },

    updateTabCount: function () {
      this.$('.js-scope .n').text('');
      this.$('.js-scope.on .n').text(this.total || '');
    },

    renderFooter: function () {
      var shown = this.items.length;
      var more = shown < this.total && !this.isKanban() ?
        '<button type="button" class="js-more pb-more">' + _.i18n('common.showMore', null, 'Show more') + '</button>' : '';
      this.ui.footer.html('<span class="pb-total"><b>' + shown + '</b> ' + _.i18n('common.of', null, 'of') +
        ' <b>' + this.total + '</b> ' + _.i18n('prospection.prospects', null, 'prospects') + '</span>' + more);
    },

    rowHtml: function (p) {
      var name = p.customerName || p.companyName || p.customerEmail || p.customerEndUserId || '?';
      var stage = byKey(STAGES, p.stage) || STAGES[0];
      var phone = byKey(PHONE, p.phoneStatus) || PHONE[0];
      var email = byKey(EMAIL, p.emailStatus) || EMAIL[0];
      var ownerName = userDisplayName(p.assignedUser);
      var lastBy = userDisplayName(p.lastActivityUser);
      var viewer = p.viewedByUser && p.viewedByUser.secId !== currentUserSecId() ? userDisplayName(p.viewedByUser) : '';
      var nextTone = !p.nextActionAt ? 'none' : (p.overdue ? 'bad' : (p.nextActionInDays === 0 ? 'today' : 'ok'));
      return ROW_TPL({
        secId: _.escape(p.secId),
        score: p.score || 0,
        fill: gaugeFill(p.score),
        heat: gaugeHeat(p.score || 0),
        scoreReason: _.escape(p.scoreReason || ''),
        avatar: _.escape(initials(name)),
        avatarColor: avatarColor(name),
        name: _.escape(name),
        isLead: p.customerType === 'lead',
        leadLabel: _.i18n('prospection.lead', null, 'Lead'),
        vip: !!p.customerVip,
        viewer: _.escape(viewer ? initials(viewer) : ''),
        viewerTitle: _.escape(viewer ? viewer + ' ' + _.i18n('prospection.viewing', null, 'has this file open') : ''),
        sub: _.escape(subLine(p)),
        potential: _.escape(potentialLine(p)),
        potentialTitle: _.escape(potentialTitle(p)),
        stage: _.escape(stage.label),
        stageTone: stage.tone,
        phone: _.escape(phone.label),
        phoneTone: phone.tone,
        phoneAttempts: p.phoneAttempts || 0,
        phoneAge: p.lastPhoneAt ? ageLabel(p.lastPhoneAt) : '',
        phoneTitle: _.escape(phone.label + (p.lastPhoneAt ? ' — ' + _.i18n('prospection.lastCall', null, 'last call') + ' ' + formatDate(p.lastPhoneAt) : '') +
          (p.phoneAttempts ? ' · ' + p.phoneAttempts + ' ' + _.i18n('prospection.attempts', null, 'attempts') : '')),
        email: _.escape(email.label),
        emailTone: email.tone,
        emailAttempts: p.emailAttempts || 0,
        emailAge: p.lastEmailAt ? ageLabel(p.lastEmailAt) : (p.lastEmailInAt ? ageLabel(p.lastEmailInAt) : ''),
        emailTitle: _.escape(email.label + (p.lastEmailAt ? ' — ' + _.i18n('prospection.lastEmail', null, 'last email logged') + ' ' + formatDate(p.lastEmailAt) : '') +
          (p.lastEmailInAt ? ' · ' + _.i18n('prospection.lastEmailIn', null, 'received from them') + ' ' + formatDate(p.lastEmailInAt) : '')),
        next: p.nextActionAt ? dueLabel(p.nextActionAt) : '',
        nextIcon: NEXT_ICON[p.nextActionType] || 'fa-flag-o',
        nextTone: nextTone,
        nextTitle: _.escape(p.nextActionAt ? formatDate(p.nextActionAt) + (p.nextActionNote ? ' — ' + p.nextActionNote : '') :
          _.i18n('prospection.due.none', null, 'Nothing planned')),
        owner: _.escape(ownerName ? initials(ownerName) : ''),
        ownerColor: avatarColor(ownerName),
        ownerTitle: _.escape(ownerName),
        claimLabel: _.i18n('prospection.claim', null, 'Take it'),
        claimTitle: _.i18n('prospection.claim.title', null, 'Become the owner of this prospect'),
        last: _.escape(p.lastActivityAt ? ageLabel(p.lastActivityAt) + (lastBy ? ' · ' + initials(lastBy) : '') : '—'),
        lastTitle: _.escape(p.lastActivityAt ? formatDate(p.lastActivityAt) + (lastBy ? ' — ' + lastBy : '') : '')
      });
    },

    // ---- kanban -----------------------------------------------------------
    // One column per stage. The stage filter narrows the columns; closed
    // stages (won / lost) only appear when the toggle asks for them.
    kanbanStages: function () {
      var picked = this.filters.stages || [];
      var closed = !!this.filters.closed;
      return _.filter(STAGES, function (s) {
        if (picked.length) { return _.contains(picked, s.key); }
        if (s.key === 'WON' || s.key === 'LOST') { return closed; }
        return true;
      });
    },

    renderKanban: function () {
      var byStage = _.groupBy(this.items, function (p) { return p.stage || 'NEW'; });
      var html = _.map(this.kanbanStages(), function (s) {
        var cards = byStage[s.key] || [];
        return COLUMN_TPL({
          stage: s.key,
          tone: s.tone,
          label: _.escape(s.label),
          count: cards.length,
          cards: _.map(cards, this.cardHtml, this).join('')
        });
      }, this).join('');
      this.ui.body.html(html);
      var shown = this.items.length;
      this.ui.footer.html(shown < this.total ?
        '<span class="pb-total">' + _.i18n('prospection.kanban.capped', null, 'Showing the') + ' <b>' + shown + '</b> ' +
        _.i18n('prospection.kanban.cappedOf', null, 'highest-scored of') + ' <b>' + this.total + '</b> — ' +
        _.i18n('prospection.kanban.narrow', null, 'narrow the filters to see the rest') + '</span>' :
        '<span class="pb-total"><b>' + shown + '</b> ' + _.i18n('prospection.prospects', null, 'prospects') + '</span>');
    },

    cardHtml: function (p) {
      var name = p.customerName || p.companyName || p.customerEmail || p.customerEndUserId || '?';
      var phone = byKey(PHONE, p.phoneStatus) || PHONE[0];
      var email = byKey(EMAIL, p.emailStatus) || EMAIL[0];
      var ownerName = userDisplayName(p.assignedUser);
      var lastBy = userDisplayName(p.lastActivityUser);
      var viewer = p.viewedByUser && p.viewedByUser.secId !== currentUserSecId() ? userDisplayName(p.viewedByUser) : '';
      var nextTone = !p.nextActionAt ? 'none' : (p.overdue ? 'bad' : (p.nextActionInDays === 0 ? 'today' : 'ok'));
      var sub = _.compact([p.companyName && p.companyName !== name ? p.companyName : '',
        $.trim((p.customerFirstName || '') + ' ' + (p.customerLastName || '')), p.customerCountryCode ? String(p.customerCountryCode).toUpperCase() : '']).join(' · ');
      return CARD_TPL({
        secId: _.escape(p.secId),
        score: p.score || 0,
        fill: gaugeFill(p.score),
        heat: gaugeHeat(p.score || 0),
        scoreReason: _.escape(p.scoreReason || ''),
        name: _.escape(name),
        isLead: p.customerType === 'lead',
        leadLabel: _.i18n('prospection.lead', null, 'Lead'),
        vip: !!p.customerVip,
        sub: _.escape(sub),
        owner: _.escape(ownerName ? initials(ownerName) : ''),
        ownerColor: avatarColor(ownerName),
        ownerTitle: _.escape(ownerName),
        phone: _.escape(phone.label),
        phoneTone: phone.tone,
        phoneAttempts: p.phoneAttempts || 0,
        phoneTitle: _.escape(phone.label + (p.lastPhoneAt ? ' — ' + formatDate(p.lastPhoneAt) : '')),
        email: _.escape(email.label),
        emailTone: email.tone,
        emailAttempts: p.emailAttempts || 0,
        emailTitle: _.escape(email.label + (p.lastEmailAt ? ' — ' + formatDate(p.lastEmailAt) : '')),
        next: p.nextActionAt ? dueLabel(p.nextActionAt) : '',
        nextIcon: NEXT_ICON[p.nextActionType] || 'fa-flag-o',
        nextTone: nextTone,
        nextTitle: _.escape(p.nextActionAt ? formatDate(p.nextActionAt) + (p.nextActionNote ? ' — ' + p.nextActionNote : '') : ''),
        viewer: _.escape(viewer ? initials(viewer) : ''),
        viewerTitle: _.escape(viewer ? viewer + ' ' + _.i18n('prospection.viewing', null, 'has this file open') : ''),
        last: _.escape(p.lastActivityAt ? ageLabel(p.lastActivityAt) + (lastBy ? ' · ' + initials(lastBy) : '') : ''),
        lastTitle: _.escape(p.lastActivityAt ? formatDate(p.lastActivityAt) + (lastBy ? ' — ' + lastBy : '') : '')
      });
    },

    onView: function (e) {
      var view = $(e.currentTarget).attr('data-view') || 'list';
      if ((this.filters.view || 'list') === view) { return; }
      this.filters.view = view;
      saveFilters(this.filters);
      this.$('.js-view').removeClass('on');
      $(e.currentTarget).addClass('on');
      this.fetch();
    },

    // ---- drag & drop between columns ----------------------------------------
    onDragStart: function (e) {
      var secId = $(e.currentTarget).attr('data-secid');
      this.dragging = secId;
      $(e.currentTarget).addClass('is-dragging');
      var dt = e.originalEvent && e.originalEvent.dataTransfer;
      if (dt) {
        dt.effectAllowed = 'move';
        try { dt.setData('text/plain', secId); } catch (err) { /* IE */ }
      }
    },

    onDragEnd: function (e) {
      $(e.currentTarget).removeClass('is-dragging');
      this.$('.pb-col').removeClass('is-over');
      this.dragging = null;
    },

    onDragOver: function (e) {
      if (!this.dragging) { return; }
      e.preventDefault();
      var dt = e.originalEvent && e.originalEvent.dataTransfer;
      if (dt) { dt.dropEffect = 'move'; }
      $(e.currentTarget).addClass('is-over');
    },

    onDragLeave: function (e) {
      if (!$.contains(e.currentTarget, e.relatedTarget)) {
        $(e.currentTarget).removeClass('is-over');
      }
    },

    // Dropping a card on a column moves the prospect to that stage; LOST asks why.
    onDrop: function (e) {
      e.preventDefault();
      var $col = $(e.currentTarget);
      $col.removeClass('is-over');
      var secId = this.dragging;
      this.dragging = null;
      var stage = $col.attr('data-stage');
      var item = _.findWhere(this.items, { secId: secId });
      if (!item || !stage || item.stage === stage) { return; }
      var reason = null;
      if (stage === 'LOST') {
        reason = window.prompt(_.i18n('prospection.lostReason', null, 'Why is it lost? (optional)'), '');
        if (reason === null) { return; }
      }
      // Move the card at once; the server answer confirms (or puts it back).
      var $card = this.$('.pb-card[data-secid="' + secId + '"]');
      $col.find('.pb-col-cards').prepend($card);
      this.refreshColumnCounts();
      var self = this;
      App.request('prospection:stage', secId, stage, reason, null).done(function (json) {
        if (self.isDestroyed) { return; }
        self.replaceRow(json);
        self.fetchSummary();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        self.renderKanban();
      });
    },

    refreshColumnCounts: function () {
      this.$('.pb-col').each(function () {
        var $c = $(this);
        $c.find('.pb-col-n').text($c.find('.pb-card').length);
      });
    },

    // Patch one row in place (the window just changed it), keeping the order.
    replaceRow: function (json) {
      if (!json || !json.secId) { return; }
      var idx = -1;
      _.each(this.items, function (it, i) { if (it.secId === json.secId) { idx = i; } });
      if (idx < 0) { return; }
      this.items[idx] = json;
      if (this.isKanban()) {
        this.renderKanban();
        return;
      }
      this.$('.pb-row[data-secid="' + json.secId + '"]').replaceWith(this.rowHtml(json));
    },

    // ---- interactions -----------------------------------------------------
    onScope: function (e) {
      this.filters.scope = $(e.currentTarget).data('scope');
      saveFilters(this.filters);
      this.$('.js-scope').removeClass('on');
      $(e.currentTarget).addClass('on');
      this.fetch();
    },

    onDue: function (e) { this.setFilter('due', $(e.currentTarget).val()); },
    onSegment: function (e) { this.setFilter('segment', $(e.currentTarget).val()); },
    onPhone: function (e) { this.setFilter('phone', $(e.currentTarget).val()); },
    onEmail: function (e) { this.setFilter('email', $(e.currentTarget).val()); },
    onSortChange: function (e) { this.setFilter('sort', $(e.currentTarget).val()); },
    onClosed: function (e) { this.setFilter('closed', e.currentTarget.checked ? true : null); },

    setFilter: function (key, value) {
      this.filters[key] = value || null;
      saveFilters(this.filters);
      this.fetch();
    },

    onRefresh: function () {
      this.fetch();
      this.fetchSummary();
    },

    onSearchKey: function (e) {
      var self = this;
      var value = $.trim($(e.currentTarget).val());
      if (e.keyCode === 13) {
        this.applySearch(value);
        return;
      }
      if (this.searchTimer) { window.clearTimeout(this.searchTimer); }
      this.searchTimer = window.setTimeout(function () { self.applySearch(value); }, 400);
    },

    applySearch: function (value) {
      if ((this.filters.search || '') === value) { return; }
      this.filters.search = value || null;
      saveFilters(this.filters);
      this.fetch();
    },

    // A KPI tile is a saved filter: click it and the board becomes that list.
    onKpiClick: function (e) {
      var filter = this.kpiFilters[$(e.currentTarget).attr('data-key')];
      if (!filter) { return; }
      this.filters.scope = filter.scope;
      this.filters.due = filter.due || null;
      saveFilters(this.filters);
      this.$('.js-scope').removeClass('on');
      this.$('.js-scope[data-scope="' + filter.scope + '"]').addClass('on');
      this.$('.js-due').val(filter.due || '');
      this.fetch();
    },

    onMore: function () {
      this.page += 1;
      this.fetchMore();
    },

    onRowClick: function (e) {
      if ($(e.target).closest('.js-claim').length) { return; }
      var secId = $(e.currentTarget).attr('data-secid');
      if (!secId) { return; }
      var item = _.findWhere(this.items, { secId: secId });
      require(['prospectionWindowView'], function (WindowView) {
        WindowView.prototype.open(secId, item);
      });
    },

    onRowKey: function (e) {
      if (e.keyCode !== 13 && e.keyCode !== 32) { return; }
      e.preventDefault();
      this.onRowClick(e);
    },

    onClaim: function (e) {
      e.preventDefault();
      e.stopPropagation();
      var self = this;
      var $btn = $(e.currentTarget);
      var secId = $btn.closest('.pb-row').attr('data-secid');
      $btn.prop('disabled', true);
      App.request('prospection:assign', secId, 'me').done(function (json) {
        if (self.isDestroyed) { return; }
        self.replaceRow(json);
        self.fetchSummary();
      }).fail(function () { $btn.prop('disabled', false); });
    },

    onActivity: function () {
      App.navigate('prospection/activity', { trigger: true });
    },

    // ---- add panel --------------------------------------------------------
    onToggleAdd: function () {
      var visible = this.ui.addPanel.is(':visible');
      this.ui.addPanel.toggle(!visible);
      if (!visible) { this.ui.addSearch.focus(); }
    },

    onAddSearchKey: function (e) {
      var self = this;
      var value = $.trim($(e.currentTarget).val());
      if (this.addTimer) { window.clearTimeout(this.addTimer); }
      if (value.length < 2) { this.ui.cands.empty(); return; }
      this.addTimer = window.setTimeout(function () {
        App.request('prospection:candidates', value).done(function (rows) {
          if (self.isDestroyed) { return; }
          self.renderCandidates(rows || []);
        });
      }, 300);
    },

    renderCandidates: function (rows) {
      if (!rows.length) {
        this.ui.cands.html('<li class="pb-cand-none">' + _.i18n('prospection.add.none', null, 'No customer or lead matches') + '</li>');
        return;
      }
      this.ui.cands.html(_.map(rows, function (r) {
        var name = r.name || r.companyName || r.email || r.endUserId || '?';
        var sub = _.compact([$.trim((r.firstName || '') + ' ' + (r.lastName || '')), r.email, r.tel, r.countryCode]).join(' · ');
        var already = !!r.prospectionSecId;
        return '<li class="pb-cand' + (already ? ' is-in' : ' js-cand') + '" data-secid="' + _.escape(r.secId) + '"' +
          ' data-prospection="' + _.escape(r.prospectionSecId || '') + '">' +
          '<span class="pb-av small ' + avatarColor(name) + '">' + _.escape(initials(name)) + '</span>' +
          '<span class="pb-cand-main"><b>' + _.escape(name) + '</b>' +
          (r.userType === 'lead' ? '<span class="pb-tag lead">' + _.i18n('prospection.lead', null, 'Lead') + '</span>' : '') +
          '<small>' + _.escape(sub) + '</small></span>' +
          '<span class="pb-cand-act">' + (already ?
            _.i18n('prospection.add.already', null, 'Already on the board') :
            '<i class="fa fa-plus"></i> ' + _.i18n('prospection.add', null, 'Add')) + '</span></li>';
      }).join(''));
    },

    onCandidateClick: function (e) {
      var self = this;
      var $li = $(e.currentTarget);
      var secId = $li.attr('data-secid');
      $li.addClass('busy');
      App.request('prospection:enroll', { endUserSecIds: [secId] }).done(function (result) {
        if (self.isDestroyed) { return; }
        $li.removeClass('busy js-cand').addClass('is-in').find('.pb-cand-act')
          .text(_.i18n('prospection.add.done', null, 'Added'));
        var created = (result && result.items) || [];
        if (created.length) {
          App.trigger('prospection:enrolled', created[0]);
        }
      }).fail(function () { $li.removeClass('busy'); });
    },

    onEnrollLeads: function (e) {
      var self = this;
      var $btn = $(e.currentTarget);
      $btn.prop('disabled', true);
      App.request('prospection:enroll', { allLeads: true }).done(function (result) {
        if (self.isDestroyed) { return; }
        $btn.prop('disabled', false);
        var remaining = (result && result.remainingLeads) || 0;
        self.ui.leadsN.text(remaining ? '(' + remaining + ')' : '');
        self.fetch();
        self.fetchSummary();
      }).fail(function () { $btn.prop('disabled', false); });
    },

    onSync: function (e) {
      var self = this;
      var $btn = $(e.currentTarget);
      $btn.prop('disabled', true);
      App.request('prospection:sync').done(function () {
        if (self.isDestroyed) { return; }
        $btn.prop('disabled', false);
        self.fetch();
        self.fetchSummary();
      }).fail(function () { $btn.prop('disabled', false); });
    },

    // ---- import ---------------------------------------------------------
    onImportTemplate: function (e) {
      e.preventDefault();
      window.open(App.request('prospection:import-template-url'), '_self');
    },

    onImport: function (e) {
      var self = this;
      var input = e.currentTarget;
      var file = input.files && input.files[0];
      if (!file) { return; }
      var $label = $(input).closest('.pb-import');
      $label.addClass('busy');
      this.ui.importResult.show().removeClass('bad').html('<i class="fa fa-spinner fa-spin"></i> ' +
        _.escape(_.i18n('prospection.import.running', null, 'Importing') + ' ' + file.name + '…'));
      App.request('prospection:import', file).done(function (result) {
        if (self.isDestroyed) { return; }
        self.renderImportResult(result || {});
        self.fetch();
        self.fetchSummary();
      }).fail(function (xhr) {
        if (self.isDestroyed) { return; }
        var reason = xhr && xhr.responseJSON && xhr.responseJSON.error;
        self.ui.importResult.addClass('bad').text(_.i18n('prospection.import.failed', null, 'The file could not be imported') +
          (reason ? ': ' + reason : '.'));
      }).always(function () {
        $label.removeClass('busy');
        // Same file again must fire a change.
        input.value = '';
      });
    },

    renderImportResult: function (r) {
      var parts = [
        '<b>' + (r.enrolled || 0) + '</b> ' + _.i18n('prospection.import.enrolled', null, 'added to the board'),
        '<b>' + (r.leadsCreated || 0) + '</b> ' + _.i18n('prospection.import.leadsCreated', null, 'new leads created'),
        '<b>' + (r.matched || 0) + '</b> ' + _.i18n('prospection.import.matched', null, 'existing customers found')
      ];
      if (r.alreadyOnBoard) {
        parts.push('<b>' + r.alreadyOnBoard + '</b> ' + _.i18n('prospection.import.already', null, 'already on the board'));
      }
      if (r.duplicates) {
        parts.push('<b>' + r.duplicates + '</b> ' + _.i18n('prospection.import.duplicates', null, 'duplicate lines ignored'));
      }
      var html = '<div class="pb-import-sum"><i class="fa fa-check-circle"></i> ' +
        (r.lines || 0) + ' ' + _.i18n('prospection.import.lines', null, 'lines read') + ' — ' + parts.join(' · ') +
        '<button type="button" class="pb-x js-import-close">×</button></div>';
      var issues = _.map(r.errors || [], function (x) { return { x: x, cls: 'bad' }; })
        .concat(_.map(r.warnings || [], function (x) { return { x: x, cls: 'warn' }; }));
      if (issues.length) {
        html += '<ul class="pb-import-issues">' + _.map(issues, function (it) {
          return '<li class="' + it.cls + '">' + _.i18n('prospection.import.line', null, 'Line') + ' ' + it.x.line +
            (it.x.label ? ' (' + _.escape(it.x.label) + ')' : '') + ': ' + _.escape(it.x.message) + '</li>';
        }).join('') + '</ul>';
      }
      this.ui.importResult.removeClass('bad').html(html).show();
      var self = this;
      this.ui.importResult.find('.js-import-close').one('click', function () { self.ui.importResult.hide().empty(); });
    },

    // ---- events from the window ------------------------------------------
    onProspectUpdated: function (json) {
      this.replaceRow(json);
      this.fetchSummary();
    },

    onProspectRemoved: function (secId) {
      this.items = _.reject(this.items, function (it) { return it.secId === secId; });
      this.total = Math.max(0, this.total - 1);
      if (this.isKanban()) {
        this.renderKanban();
        this.updateTabCount();
        this.fetchSummary();
        return;
      }
      this.$('.pb-row[data-secid="' + secId + '"]').remove();
      this.renderFooter();
      this.updateTabCount();
      this.fetchSummary();
    },

    onProspectEnrolled: function (json) {
      if (!json || _.findWhere(this.items, { secId: json.secId })) { return; }
      this.items.unshift(json);
      this.total += 1;
      if (this.isKanban()) {
        this.renderKanban();
        this.updateTabCount();
        this.fetchSummary();
        return;
      }
      this.$('.pb-state').remove();
      this.ui.body.prepend(this.rowHtml(json));
      this.renderFooter();
      this.updateTabCount();
      this.fetchSummary();
    },

    onDestroy: function () {
      if (this.searchTimer) { window.clearTimeout(this.searchTimer); }
      if (this.addTimer) { window.clearTimeout(this.addTimer); }
      if (this.assigneeDropdown) { this.assigneeDropdown.destroy(); this.assigneeDropdown = null; }
      if (this.stageDropdown) { this.stageDropdown.destroy(); this.stageDropdown = null; }
      if (this.webshopDropdown) { this.webshopDropdown.destroy(); this.webshopDropdown = null; }
    }
  });
});

